import { useEffect, useMemo, useRef, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  TextInput,
  Textarea,
  Checkbox,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Radio,
  Badge,
  Stepper,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { RADIOLOGY_DISCOVERY_MODALITIES } from '@/model/middleware-types'
import { useCanOrder } from './shared/useCanOrder'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapRadiologyToPdf } from './pdf/mapRadiologyToPdf'
import { loadLogoDataUrl } from './shared/loadLogoDataUrl'
import {
  RADIOLOGY_QUESTIONNAIRE_ID,
  RADIOLOGY_MODALITY_OPTIONS,
  createInitialRadiologyFormState,
} from './radiologyFormData'
import type { RadiologyFormState } from './radiologyFormData'
import type { AutoPopulateData } from './pdf/pdfTypes'
import type { RadiologyFacilityWithModalities } from './shared/useRadiologyFacilities'
import { discoveryModalityForProcedures, type DiscoveryModality } from './discovery/discoveryTypes'
import { RadiologyCompendiumSection } from './RadiologyCompendiumSection'
import { RadiologyFacilityStep } from './RadiologyFacilityStep'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { useSaveDraftCoordinatorFlow } from './shared/useSaveDraftCoordinatorFlow'
import {
  draftOnlyReason,
  SUBMIT_PERMISSION_TOOLTIP,
  SIGNATURE_REQUIRED_TOOLTIP,
} from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'

type Props = {
  patient: Patient
  onBack: () => void
}

type Step = 'procedures' | 'facility' | 'details' | 'review'
const STEPS: Step[] = ['procedures', 'facility', 'details', 'review']

export const RadiologyRequisitionForm = ({ patient, onBack }: Props) => {
  const [step, setStep] = useState<Step>('procedures')
  const [patientChoosesLocation, setPatientChoosesLocation] = useState(false)
  // PD-1929 — fax delivery chosen before a facility is picked. Kept here, not in the
  // delivery step, so going Back to change the tests doesn't forget it (Greptile !2060).
  const [faxDeliveryChosen, setFaxDeliveryChosen] = useState(false)

  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const medplum = useMedplum()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const [selectedFacility, setSelectedFacility] = useState<RadiologyFacilityWithModalities | null>(
    null,
  )

  const form = useForm<RadiologyFormState>({
    initialValues: createInitialRadiologyFormState(),
    validateInputOnBlur: true,
    validate: {
      providerSignatureDate: (v: string) =>
        v && !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v) ? 'Date must be MM/DD/YYYY' : null,
    },
  })

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: RADIOLOGY_QUESTIONNAIRE_ID,
    generatePdfBytes: async () => {
      const logoDataUrl = selectedFacility?.logoUrl
        ? await loadLogoDataUrl(medplum, selectedFacility.logoUrl)
        : undefined
      return generateBeHumanOrderPdf(
        mapRadiologyToPdf(
          form.values,
          autoPopulateData,
          selectedFacility ?? undefined,
          logoDataUrl,
          patientChoosesLocation,
        ),
      )
    },
    buildFileName: () =>
      `Radiology_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () =>
      ({
        ...form.values,
        performerOrganizationId: selectedFacility?.id,
        // PD-1642 — tells middleware this requisition is patient-carried, so the PDF
        // is stamped `LabRequisition` and surfaces in the patient portal instead of
        // being hidden there as `Order`. The chart shows it under Orders only
        // (PD-1745): Documents excludes the code so the order is not listed twice.
        patientChoosesLocation,
        selectedTests: form.values.selectedProcedures.map((p) => p.code),
        icd10Codes: form.values.diagnosisText
          .split(/[;\n]/)
          .map((s) => s.trim().split(/\s/)[0]?.toUpperCase())
          .filter(Boolean),
      }) as unknown as Record<string, unknown>,
    vendorName: 'Radiology',
    laboratoryOrganizationName: 'Radiology',
    facilityName: selectedFacility?.name,
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
    overrideFaxNumber: selectedFacility ? selectedFacility.fax || '' : null,
    // PD-1642 — nothing is transmitted when the patient picks the location, so skip
    // the fax lookup (which would otherwise block on "no fax available") and the
    // /order/send-fax call. /order/create still runs.
    skipFax: patientChoosesLocation,
  })

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.providerSignatureDataUrl) {
      form.setFieldValue('providerSignatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'providerSignatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('providerSignatureDataUrl', dataUrl)
    form.setFieldValue('providerSignatureDate', date)
    signature.closeSignatureModal()
  }

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }
    if (mustSaveAsDraft) {
      notifications.show({
        title: 'Cannot Submit',
        message: facilityPendingApproval
          ? 'This facility is pending admin approval. Use "Save as Draft" instead.'
          : 'The selected facility has no fax number on file. Use "Save as Draft" instead.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  const facilityPendingApproval =
    selectedFacility && 'pendingApproval' in selectedFacility && selectedFacility.pendingApproval
  const noFacilitySelected = !selectedFacility
  // PD-1642 — a patient-chosen location has no facility by design, so none of the
  // facility gates apply and the order can be submitted outright.
  const mustSaveAsDraft =
    !patientChoosesLocation &&
    (noFacilitySelected || (selectedFacility && !selectedFacility.fax) || !!facilityPendingApproval)

  const coordinatorFlow = useSaveDraftCoordinatorFlow({
    patient,
    orderType: 'Radiology',
    skipFacility: false,
    coordinatorNote: '',
    alreadySaved: submission.orderSubmitted,
    submitting: submission.submitting,
    onSaveDraft: submission.handleSaveDraft,
  })

  const handleSaveDraft = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before saving.',
        color: 'red',
      })
      return
    }
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before saving.',
        color: 'red',
      })
      return
    }
    await coordinatorFlow.triggerSaveDraft()
  }

  const stepIndex = STEPS.indexOf(step)
  const canAdvanceFromProcedures = form.values.selectedProcedures.length > 0
  // PD-1939 — a faxed order needs the fax number before Details, not a draft at Review.
  const canAdvanceFromFacility =
    patientChoosesLocation || (!!form.values.selectedFacilityId && !!selectedFacility?.fax)

  // RadiologyProcedure.modality holds the LABEL (e.g. 'CT Scan'), not the key — map it.
  // The facility search is scoped to the modality the clinician already chose in step 1.
  const discoveryModality = useMemo<DiscoveryModality | undefined>(
    () =>
      discoveryModalityForProcedures(
        form.values.selectedProcedures.map((p) => p.modality),
        RADIOLOGY_MODALITY_OPTIONS,
        RADIOLOGY_DISCOVERY_MODALITIES,
      ),
    [form.values.selectedProcedures],
  )

  // A facility chosen for one modality must not survive a change of procedures: going
  // back and swapping CT for MRI would otherwise let the order advance — and fax — to
  // the CT facility. Skipped on first render so a restored selection is kept.
  const lastModality = useRef(discoveryModality)
  useEffect(() => {
    if (lastModality.current === discoveryModality) return
    lastModality.current = discoveryModality
    if (form.values.selectedFacilityId || selectedFacility) {
      form.setFieldValue('selectedFacilityId', '')
      setSelectedFacility(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the modality only
  }, [discoveryModality])

  const handleSelectFacility = (id: string, facility: RadiologyFacilityWithModalities | null) => {
    // PD-1642 — picking a facility is the other half of the either/or.
    setPatientChoosesLocation(false)
    form.setFieldValue('selectedFacilityId', id)
    if (facility) setSelectedFacility(facility)
  }

  const handleBack = () => {
    const idx = STEPS.indexOf(step)
    if (idx === 0) {
      onBack()
    } else {
      setStep(STEPS[idx - 1])
    }
  }

  // Group selected procedures by modality for the review summary
  const proceduresByModality = useMemo(() => {
    const grouped: Record<string, string[]> = {}
    for (const proc of form.values.selectedProcedures) {
      if (!grouped[proc.modality]) grouped[proc.modality] = []
      grouped[proc.modality].push(proc.name)
    }
    return grouped
  }, [form.values.selectedProcedures])

  const cartItems: CartItem[] = useMemo(
    () =>
      form.values.selectedProcedures.map((p) => ({
        id: p.code,
        code: p.code,
        name: p.name,
        category: p.modality,
      })),
    [form.values.selectedProcedures],
  )

  const handleCartRemove = (id: string) => {
    form.setFieldValue(
      'selectedProcedures',
      form.values.selectedProcedures.filter((p) => p.code !== id),
    )
  }

  return (
    <OrderFormLayout
      // The discovery facility step gives its column to the list + map instead of the cart.
      cart={
        step === 'facility' ? null : (
          <OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="Radiology" />
        )
      }
      header={
        <>
          <FormHeader
            title="Radiology Order Requisition"
            subtitle="Select procedures, choose a facility, and complete the order"
            onBack={handleBack}
            onPreview={submission.handlePreviewPdf}
            onSubmit={handleSubmitOrder}
            exporting={submission.exporting}
            submitting={submission.submitting}
            orderSubmitted={submission.orderSubmitted}
            canSubmit={isPractitioner && step === 'review' && !mustSaveAsDraft}
            canOrderLoading={canOrderLoading}
            hideActions
            submitTooltip={
              !isPractitioner
                ? 'Practitioner permissions required to submit orders'
                : step !== 'review'
                  ? 'Complete all steps to submit'
                  : undefined
            }
            labError={submission.labError}
            orgError={orgError}
            warnings={insuranceWarning ? [insuranceWarning] : []}
          />

          <Stepper active={stepIndex} mb="lg" size="sm">
            <Stepper.Step label="Procedures" description="Select imaging studies" />
            <Stepper.Step label="Order delivery" description="Patient or fax" />
            <Stepper.Step label="Details" description="Clinical info & signature" />
            <Stepper.Step label="Review" description="Review & submit" />
          </Stepper>
        </>
      }
    >
      {/* ===== STEP 1: PROCEDURES ===== */}
      {step === 'procedures' && (
        <Stack gap="lg">
          <RadiologyCompendiumSection
            selectedProcedures={form.values.selectedProcedures}
            onSelectionChange={(procs) => form.setFieldValue('selectedProcedures', procs)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={() => setStep('facility')}
              disabled={!canAdvanceFromProcedures}
            >
              Next: Order Delivery
            </Button>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 2: FACILITY ===== */}
      {step === 'facility' && (
        <RadiologyFacilityStep
          patient={patient}
          selectedFacilityId={form.values.selectedFacilityId}
          modality={discoveryModality}
          patientChoosesLocation={patientChoosesLocation}
          onPatientChoosesLocation={() => {
            setPatientChoosesLocation(true)
            setFaxDeliveryChosen(false)
            form.setFieldValue('selectedFacilityId', '')
            setSelectedFacility(null)
          }}
          onFaxDelivery={() => {
            setPatientChoosesLocation(false)
            setFaxDeliveryChosen(true)
          }}
          faxDeliveryChosen={faxDeliveryChosen}
          onSelectFacility={handleSelectFacility}
          selectedFacilityFax={selectedFacility?.fax || undefined}
          canAdvance={canAdvanceFromFacility}
          onBack={() => setStep('procedures')}
          onNext={() => setStep('details')}
        />
      )}

      {/* ===== STEP 3: DETAILS ===== */}
      {step === 'details' && (
        <Stack gap="lg">
          <DiagnosisCodesSection
            value={form.values.diagnosisText}
            defaultCodes={diagnosisDefaults}
            onChange={(v) => form.setFieldValue('diagnosisText', v)}
          />

          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              CLINICAL INFORMATION
            </Text>
            <Stack gap="sm">
              <Textarea
                label="Clinical Indication / Reason for Exam"
                placeholder="Enter clinical indication for the imaging study..."
                minRows={3}
                {...form.getInputProps('clinicalNotes')}
              />
              <Textarea
                label="Relevant Clinical History"
                placeholder="Enter relevant clinical history..."
                minRows={2}
                {...form.getInputProps('relevantHistory')}
              />
              <Radio.Group label="Is the patient pregnant?" {...form.getInputProps('isPregnant')}>
                <Group mt={4}>
                  <Radio value="no" label="No" />
                  <Radio value="yes" label="Yes" />
                  <Radio value="na" label="N/A" />
                </Group>
              </Radio.Group>
              <Checkbox
                label="Known contrast/dye allergy"
                {...form.getInputProps('contrastAllergy', { type: 'checkbox' })}
              />
              <TextInput
                label="Prior Authorization Code"
                placeholder="Enter if applicable"
                {...form.getInputProps('priorAuthCode')}
              />
            </Stack>
          </Paper>

          <SignatureSection
            signatureDataUrl={form.values.providerSignatureDataUrl}
            signatureDate={form.values.providerSignatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="I am a licensed healthcare provider and certify that the ordered imaging studies are medically necessary."
          />

          <Group justify="space-between">
            <Button variant="default" onClick={() => setStep('facility')}>
              Back
            </Button>
            <TooltipButton
              color="teal"
              disabled={submission.orderSubmitted || !form.values.providerSignatureDataUrl}
              disabledReason={
                !form.values.providerSignatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null
              }
              onClick={() => setStep('review')}
            >
              Review Order
            </TooltipButton>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 4: REVIEW ===== */}
      {step === 'review' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              ORDER SUMMARY
            </Text>

            {/* Selected procedures */}
            <Text fw={500} size="sm" mb="xs">
              Procedures ({form.values.selectedProcedures.length})
            </Text>
            <Stack gap="xs" mb="md">
              {Object.entries(proceduresByModality).map(([modality, names]) => (
                <div key={modality}>
                  <Text size="xs" fw={600} c="dimmed">
                    {modality}
                  </Text>
                  <Group gap={4} mt={2}>
                    {names.map((name) => (
                      <Badge key={name} size="sm" variant="light" color="blue">
                        {name}
                      </Badge>
                    ))}
                  </Group>
                </div>
              ))}
            </Stack>

            {/* Selected facility */}
            <Text fw={500} size="sm" mb="xs">
              Facility
            </Text>
            {patientChoosesLocation ? (
              <div className="mb-md rounded-md border border-blue-200 bg-blue-50 p-3">
                <Text fw={600} size="sm" c="blue">
                  Patient will choose a radiology location
                </Text>
                <Text size="xs" mt={4}>
                  No facility fax is required. The patient receives a printable requisition valid at
                  any radiology location offering the ordered procedures.
                </Text>
              </div>
            ) : selectedFacility ? (
              <div className="mb-md rounded-md border border-gray-200 p-3">
                <Text fw={600} size="sm">
                  {selectedFacility.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {selectedFacility.address}, {selectedFacility.city}, {selectedFacility.state}{' '}
                  {selectedFacility.zip}
                </Text>
                {selectedFacility.fax && (
                  <Text size="xs" c="dimmed">
                    Fax: {selectedFacility.fax}
                  </Text>
                )}
              </div>
            ) : null}

            {/* Diagnosis */}
            {form.values.diagnosisText && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Diagnosis Codes
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                  {form.values.diagnosisText}
                </Text>
              </>
            )}

            {/* Clinical notes */}
            {form.values.clinicalNotes && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Clinical Indication
                </Text>
                <Text size="xs" c="dimmed">
                  {form.values.clinicalNotes}
                </Text>
              </>
            )}

            {form.values.relevantHistory && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Relevant History
                </Text>
                <Text size="xs" c="dimmed">
                  {form.values.relevantHistory}
                </Text>
              </>
            )}

            {/* Pregnancy / Allergy flags */}
            <Group gap="md" mt="md">
              {form.values.isPregnant && (
                <Badge
                  size="sm"
                  variant="light"
                  color={form.values.isPregnant === 'yes' ? 'red' : 'gray'}
                >
                  Pregnant:{' '}
                  {form.values.isPregnant === 'yes'
                    ? 'Yes'
                    : form.values.isPregnant === 'no'
                      ? 'No'
                      : 'N/A'}
                </Badge>
              )}
              {form.values.contrastAllergy && (
                <Badge size="sm" variant="light" color="red">
                  Contrast Allergy
                </Badge>
              )}
              {form.values.priorAuthCode && (
                <Badge size="sm" variant="light" color="green">
                  Prior Auth: {form.values.priorAuthCode}
                </Badge>
              )}
            </Group>

            {/* Signature */}
            {form.values.providerSignatureDataUrl && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Provider Signature
                </Text>
                <img
                  src={form.values.providerSignatureDataUrl}
                  alt="Provider signature"
                  className="h-12 rounded border border-gray-200 p-1"
                />
                <Text size="xs" c="dimmed">
                  Signed: {form.values.providerSignatureDate}
                </Text>
              </>
            )}
          </Paper>

          {mustSaveAsDraft && !submission.orderSubmitted && (
            <Text size="sm" c="orange" mb="xs">
              {draftOnlyReason({
                usingCoordinatorNote: false,
                facilityPendingApproval: !!facilityPendingApproval,
              })}
            </Text>
          )}

          <Group justify="space-between" pb="xl">
            <Button variant="default" onClick={() => setStep('details')}>
              Back
            </Button>
            <Group gap="sm">
              <Button
                variant="outline"
                onClick={submission.handlePreviewPdf}
                loading={submission.exporting}
              >
                Download PDF
              </Button>
              {mustSaveAsDraft ? (
                <Button
                  color="blue"
                  onClick={handleSaveDraft}
                  loading={submission.submitting}
                  disabled={
                    submission.orderSubmitted ||
                    !isPractitioner ||
                    !form.values.providerSignatureDataUrl
                  }
                >
                  {submission.orderSubmitted ? 'Draft Saved' : 'Save as Draft'}
                </Button>
              ) : (
                <TooltipButton
                  color="teal"
                  loading={submission.submitting}
                  disabled={
                    submission.orderSubmitted ||
                    !isPractitioner ||
                    !form.values.providerSignatureDataUrl
                  }
                  disabledReason={
                    !isPractitioner
                      ? SUBMIT_PERMISSION_TOOLTIP
                      : !form.values.providerSignatureDataUrl
                        ? SIGNATURE_REQUIRED_TOOLTIP
                        : null
                  }
                  onClick={handleSubmitOrder}
                >
                  {submission.orderSubmitted ? 'Order Submitted' : 'Submit Order'}
                </TooltipButton>
              )}
            </Group>
          </Group>
        </Stack>
      )}
      {coordinatorFlow.modal}
    </OrderFormLayout>
  )
}
